class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const gridContainer = document.getElementById('ProductGridContainer');

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    if (gridContainer && gridContainer.querySelector('.collection')) {
      gridContainer.querySelector('.collection').classList.add('loading');
    }

    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;

        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];

        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(html.innerHTML);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(html.innerHTML);
    }
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsedHTML.getElementById('ProductGridContainer');
    const currentGrid = document.getElementById('ProductGridContainer');

    if (!newGrid || !currentGrid) return;

    currentGrid.innerHTML = newGrid.innerHTML;

    currentGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newCount = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (newCount && container) {
      container.innerHTML = newCount.innerHTML;
      container.classList.remove('loading');
    }

    if (newCount && containerDesktop) {
      containerDesktop.innerHTML = newCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];

          if (elementToRender.className === previousElementClassName) {
            const previousElement = document.getElementById(previousElementId);
            if (previousElement) previousElement.after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parentFilter = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (parentFilter) parentFilter.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.keepMobileFacetsExpanded();

    if (countsToRender && event && event.target) {
      const closestFilter = event.target.closest('.js-filter');

      if (closestFilter && closestFilter.id) {
        FacetFiltersForm.renderCounts(countsToRender, closestFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestFilter.id));

        const newFacetDetailsElement = document.getElementById(closestFilter.id);

        if (newFacetDetailsElement) {
          const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
            ? '.mobile-facets__close-button'
            : '.facets__summary';

          const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
          const inputType = event.target.getAttribute('type');
          const isRangeInput = inputType === 'range';
          const isTextInput = inputType === 'text';

          if (newElementToActivate && !isTextInput && !isRangeInput) {
            newElementToActivate.focus();
          }
        }
      }
    }

    FacetFiltersForm.initPriceRanges();
    FacetFiltersForm.keepMobileFacetsExpanded();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentActiveFacetsElement = document.querySelector(selector);

      if (!activeFacetsElement || !currentActiveFacetsElement) return;

      currentActiveFacetsElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const newElement = html.querySelector(selector);
      const currentElement = document.querySelector(selector);

      if (!newElement || !currentElement) return;

      currentElement.innerHTML = newElement.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm ? mobileForm.closest('menu-drawer') : null;

    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }
  }

  static keepMobileFacetsExpanded() {
    const detailsElements = document.querySelectorAll('#FacetFiltersFormMobile details.mobile-facets__details');

    detailsElements.forEach((details) => {
      details.open = true;
    });

    if (detailsElements.length) {
      console.debug('[PrettyJ facets] mobile details forced open:', detailsElements.length);
    }
  }

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));

      if (isShowingMore) {
        sourceWrapElement.querySelectorAll('.facets__item.hidden').forEach((hiddenItem) => {
          hiddenItem.classList.replace('hidden', 'show-more-item');
        });
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }

    const targetPrice = target.querySelector('.facets__price');
    const sourcePrice = source.querySelector('.facets__price');

    if (sourcePrice && targetPrice) {
      targetPrice.outerHTML = sourcePrice.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!source || !target) return;

    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }

    const targetPrice = target.querySelector('.facets__price');
    const sourcePrice = source.querySelector('.facets__price');

    if (sourcePrice && targetPrice) {
      targetPrice.outerHTML = sourcePrice.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');

    return [
      {
        section: grid ? grid.dataset.id : '',
      },
    ];
  }

  static initPriceRanges() {
    document.querySelectorAll('price-range').forEach((priceRange) => {
      if (typeof priceRange.updatePriceRangeUI === 'function') {
        priceRange.setMinAndMaxValues();
        priceRange.updatePriceRangeUI();
      }
    });
  }

  createSearchParams(form) {
    const formData = new FormData(form);

    for (const [key, value] of Array.from(formData.entries())) {
      if (value === '' || value === null) {
        formData.delete(key);
      }
    }

    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    if (event.target && event.target.classList.contains('mobile-facets__checkbox')) {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
      return;
    }

    const forms = [];
    const closestForm = event.target.closest('form');
    const isMobile = closestForm && closestForm.id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}

FacetFiltersForm.setListeners();

document.addEventListener('click', (event) => {
  if (!event.target.closest('.mobile-facets__open-wrapper')) return;

  setTimeout(() => FacetFiltersForm.keepMobileFacetsExpanded(), 120);
  setTimeout(() => FacetFiltersForm.keepMobileFacetsExpanded(), 320);
});

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.inputs = Array.from(this.querySelectorAll('input'));
    this.minInput = this.querySelector('input[data-role="min"]') || this.inputs[0];
    this.maxInput = this.querySelector('input[data-role="max"]') || this.inputs[1];
    this.minHidden = this.querySelector('input[data-role="min-hidden"]');
    this.maxHidden = this.querySelector('input[data-role="max-hidden"]');
    this.currencySymbol = this.querySelector('.pj-price-range')?.dataset.currencySymbol || '';
    this.minLabel = this.querySelector('[data-pj-price-min-label]');
    this.maxLabel = this.querySelector('[data-pj-price-max-label]');

    [this.minInput, this.maxInput].filter(Boolean).forEach((element) => {
      element.addEventListener('input', this.onRangeChange.bind(this));
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    this.setMinAndMaxValues();
    this.updatePriceRangeUI();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.updatePriceRangeUI();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;

    if (!event.key.match(pattern)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    if (!this.minInput || !this.maxInput) return;

    const minValue = Number(this.minInput.value || 0);
    const maxValue = Number(this.maxInput.value || this.maxInput.getAttribute('data-max') || 0);
    const rangeMax = Number(this.maxInput.getAttribute('data-max') || 0);

    this.minInput.setAttribute('data-max', maxValue);
    this.maxInput.setAttribute('data-min', minValue);

    if (this.minHidden) {
      this.minHidden.value = minValue > 0 ? minValue : '';
      this.minHidden.disabled = minValue <= 0;
    }

    if (this.maxHidden) {
      this.maxHidden.value = maxValue < rangeMax ? maxValue : '';
      this.maxHidden.disabled = maxValue >= rangeMax;
    }
  }

  adjustToValidValues(input) {
    if (!input) return;

    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min') || 0);
    const max = Number(input.getAttribute('data-max') || 0);

    if (value < min) input.value = min;
    if (value > max) input.value = max;

    if (this.minInput && this.maxInput) {
      const minValue = Number(this.minInput.value || 0);
      const maxValue = Number(this.maxInput.value || max);

      if (minValue > maxValue) {
        if (input === this.minInput) this.minInput.value = maxValue;
        if (input === this.maxInput) this.maxInput.value = minValue;
      }
    }
  }

  updatePriceRangeUI() {
    if (!this.minInput || !this.maxInput) return;

    const minValue = Number(this.minInput.value || 0);
    const maxValue = Number(this.maxInput.value || this.maxInput.getAttribute('data-max') || 0);
    const rangeMax = Number(this.maxInput.getAttribute('data-max') || 1);

    const left = Math.max(0, Math.min(100, (minValue / rangeMax) * 100));
    const right = Math.max(0, Math.min(100, 100 - (maxValue / rangeMax) * 100));

    this.style.setProperty('--pj-range-left', `${left}%`);
    this.style.setProperty('--pj-range-right', `${right}%`);

    if (this.minLabel) this.minLabel.textContent = `${this.currencySymbol}${minValue}`;
    if (this.maxLabel) this.maxLabel.textContent = `${this.currencySymbol}${maxValue}`;
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink = this.querySelector('a');

    if (!facetLink) return;

    facetLink.setAttribute('role', 'button');

    facetLink.addEventListener('click', this.closeFilter.bind(this));

    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();

      if (event.code.toUpperCase() === 'SPACE') {
        this.closeFilter(event);
      }
    });
  }

  closeFilter(event) {
    event.preventDefault();

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (form && typeof form.onActiveFilterClick === 'function') {
      form.onActiveFilterClick(event);
    }
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}
